import { AxiosError, AxiosRequestConfig } from 'axios'
import React from 'react'
import {
  FetchInfiniteQueryOptions,
  QueryClient,
  QueryFunction,
  useInfiniteQuery,
  useQuery,
  useQueries
} from 'react-query'
import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult
} from 'react-query/types/react/types'
import { useDeepCompareEffect } from 'react-use'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { PagedResponse } from 'types/paged-response'

export const gremlinQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      cacheTime: 1_000 * 60 * 10 // 10 minutes
    }
  }
})

// List of endpoints we don't want to add `?teamId=xxxx` to
// Can be string or Regex
const excludeTeamIdList: Array<string | RegExp> = [
  // any endpoint that already includes the teamId param does not need it added
  'teamId=',
  // the rest of these endpoints do not require the teamId query param to be passed in
  // because they use the COMPANY_DEFAULT privilege instead of TEAM_DEFAULT
  // and thus do not require the teamId to be passed to them
  'providers/all',
  'apikeys',
  'users/auth/mfa/info',
  'companies/',
  /teams\/(.*)\/profile/
]

export const getEndpointWithTeamId = (
  endpoint: string,
  currentTeamId: string
) =>
  excludeTeamIdList.some((test) =>
    test instanceof RegExp ? endpoint.match(test) : endpoint.includes(test)
  )
    ? endpoint
    : endpoint.includes('?')
    ? `${endpoint}&teamId=${currentTeamId}`
    : `${endpoint}?teamId=${currentTeamId}`

export async function fetchQueryData<TQueryFnData>(
  endpoint: string,
  axiosConfig: AxiosRequestConfig
): Promise<TQueryFnData> {
  const res = await agent.get<TQueryFnData>(endpoint, axiosConfig)
  return res.data
}

export async function prefetchQuery<TQueryFnData>(
  endpoint: string,
  axiosConfig: AxiosRequestConfig
): Promise<void> {
  const fetch = async () =>
    await fetchQueryData<TQueryFnData>(endpoint, axiosConfig)

  gremlinQueryClient.prefetchQuery(endpoint, fetch)
}

export function usePrefetchQueryData(
  endpoint: string,
  axiosConfig: AxiosRequestConfig = {}
): void {
  useDeepCompareEffect(() => {
    prefetchQuery(endpoint, axiosConfig)
  }, [endpoint, axiosConfig])
}

export function useGremlinQuery<TQueryFnData, TData = TQueryFnData>(
  endpoint: string,
  axiosConfig: AxiosRequestConfig = {},
  useQueryOptions: UseQueryOptions<TQueryFnData, AxiosError, TData> = {}
): UseQueryResult<TData, AxiosError> {
  const { currentTeamId } = useAuth()

  const endpointWithTeamId = getEndpointWithTeamId(endpoint, currentTeamId)

  const fetchData = React.useCallback(
    async () =>
      await fetchQueryData<TQueryFnData>(endpointWithTeamId, axiosConfig),
    [endpointWithTeamId, axiosConfig]
  )

  // force enabled to be a boolean
  // otherwise `undefined` and other falsy values won't disable the query
  // https://github.com/TanStack/query/issues/2269
  if ('enabled' in useQueryOptions) {
    useQueryOptions.enabled = Boolean(useQueryOptions.enabled)
  }

  const fetchState = useQuery<TQueryFnData, AxiosError, TData>(
    useQueryOptions.queryKey || endpointWithTeamId,
    fetchData,
    useQueryOptions
  )

  return fetchState
}

export function useGremlinQueries<TQueryFnData, TData = TQueryFnData>(
  queries: {
    endpoint: string
    options?: UseQueryOptions<TQueryFnData, AxiosError, TData>
    teamId?: string
  }[],
  axiosConfig: AxiosRequestConfig = {}
) {
  const { currentTeamId } = useAuth()

  const queriesMapped = queries.map((queryObj, idx) => {
    const { endpoint, teamId, options = {} } = queryObj
    const teamIdForQuery = teamId ?? currentTeamId
    const endpointWithTeamId = getEndpointWithTeamId(endpoint, teamIdForQuery)
    const queryKey = queryObj.options?.queryKey ?? [endpointWithTeamId, idx]

    // todo: memoize?
    // ESLint: React Hook "React.useCallback" cannot be called inside a callback. React Hooks must be called in a React function component or a custom React Hook function.(react-hooks/rules-of-hooks)
    const fetchData = async () =>
      await fetchQueryData<TQueryFnData>(endpointWithTeamId, axiosConfig)

    // force enabled to be a boolean
    // otherwise `undefined` and other falsy values won't disable the query
    // https://github.com/TanStack/query/issues/2269
    if ('enabled' in options) {
      options.enabled = Boolean(options.enabled)
    }

    return {
      queryFn: fetchData,
      queryKey,
      ...options
    }
  })

  const fetchStates = useQueries<TQueryFnData[]>(
    queriesMapped as TQueryFnData[]
  )

  return fetchStates
}

export async function prefetchPaginationData<
  TQueryFnData extends PagedResponse
>(
  endpoint: string,
  axiosConfig: AxiosRequestConfig,
  queryOptions: FetchInfiniteQueryOptions = {}
): Promise<void> {
  const { queryKey = endpoint } = queryOptions

  return gremlinQueryClient.prefetchInfiniteQuery({
    ...queryOptions,
    queryKey,
    queryFn: async () =>
      await fetchQueryData<TQueryFnData>(endpoint, axiosConfig)
  })
}

export function usePrefetchPagintationData(
  endpoint: string,
  axiosConfig: AxiosRequestConfig = {}
): void {
  useDeepCompareEffect(() => {
    prefetchPaginationData(endpoint, axiosConfig)
  }, [endpoint, axiosConfig])
}

export function useGremlinPagination<
  TQueryFnData extends PagedResponse,
  TData = TQueryFnData
>(
  endpoint: string,
  axiosConfig: AxiosRequestConfig = {},
  options: UseInfiniteQueryOptions<TQueryFnData, AxiosError, TData> = {}
): UseInfiniteQueryResult<TData, AxiosError> {
  const { currentTeamId } = useAuth()
  const endpointWithTeamId = getEndpointWithTeamId(endpoint, currentTeamId)

  const fetchData = React.useCallback(
    async ({ pageParam = '' }) => {
      const queryParams = Object.assign(
        {},
        axiosConfig.params,
        pageParam && {
          pageToken: pageParam
        }
      )
      return await fetchQueryData(endpointWithTeamId, {
        ...axiosConfig,
        params: queryParams
      })
    },
    [endpointWithTeamId, axiosConfig]
  ) as QueryFunction<TQueryFnData>

  const fetchState = useInfiniteQuery<TQueryFnData, AxiosError, TData>(
    options.queryKey || endpointWithTeamId,
    fetchData,
    {
      getNextPageParam: (lastPage) => lastPage.pageToken,
      ...options
    }
  )

  return fetchState
}
